/* Minimog's MQuantityInput.updateQuantityRules assumes every m-quantity-input
   sits inside #QuantityForm-{sectionId}. The tile UI renders its stepper
   outside that wrapper, so closest() returns null and the method throws.
   product-info calls it midway through its variant update, so the exception
   aborts the rest of that routine - including the variantChange emit - which
   is why tile prices only refreshed on reload. Skip it for inputs with no
   QuantityForm ancestor; let it run normally otherwise. */
function guardMinimogQuantityRules() {
  const QuantityInput = customElements.get('m-quantity-input');
  if (!QuantityInput || QuantityInput.prototype.__qtyRulesGuarded) return;

  const original = QuantityInput.prototype.updateQuantityRules;
  if (typeof original !== 'function') return;

  QuantityInput.prototype.updateQuantityRules = function (sectionId, productId, parsedHTML) {
    if (!this.closest('#QuantityForm-' + sectionId)) return;
    return original.call(this, sectionId, productId, parsedHTML);
  };
  QuantityInput.prototype.__qtyRulesGuarded = true;
}

if (customElements.get('m-quantity-input')) {
  guardMinimogQuantityRules();
} else {
  customElements.whenDefined('m-quantity-input').then(guardMinimogQuantityRules);
}

if (!customElements.get('qty-tile-selector')) {
  class QtyTileSelector extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="quantity"]');
      this.customWrapper = this.querySelector('[data-qty-custom-input]');
      this.selectedLabel = this.querySelector('[data-qty-selected]');
      this.nudgeOut = this.querySelector('[data-qty-nudge-out]');
      this.tiles = Array.from(this.querySelectorAll('[data-qty-tile]'));

      this.bindTiles();

      if (this.input) {
        this.input.addEventListener('change', () => this.syncActiveTile());
      }

      /* Re-price the tiles whenever the shopper picks a different variant.
         product-info refetches the section HTML but only swaps Price /
         Inventory / Sku / Availability / Volume / PricePerItem, so the tiles
         are left showing the previous variant. The variantChange payload
         carries that fetched HTML, so we copy the freshly rendered tile
         prices + badges out of it (keeping Liquid as the pricing source). */
      if (window.MinimogEvents && window.MinimogTheme && MinimogTheme.pubSubEvents) {
        this.variantChangeUnsubscriber = MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.variantChange,
          this.onVariantChange.bind(this)
        );
      }

      this.syncActiveTile();
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
    }

    bindTiles() {
      this.tiles.forEach((tile) => {
        if (tile.__qtyBound) return;
        tile.__qtyBound = true;
        tile.addEventListener('click', () => this.onTileClick(tile));
      });
    }

    /* ---------- keep tile pricing in sync with the selected variant ---------- */
    onVariantChange(payload) {
      const html = payload && payload.data && payload.data.html;
      if (!html) return;

      const allInDoc = Array.from(document.querySelectorAll('qty-tile-selector'));
      const sources = html.querySelectorAll('qty-tile-selector');
      if (!sources.length) return;
      const idx = allInDoc.indexOf(this);
      const source = sources[idx] || sources[0];
      if (!source) return;

      const srcTiles = source.querySelectorAll('[data-qty-tile]');
      const dstTiles = this.querySelectorAll('[data-qty-tile]');
      if (!srcTiles.length || srcTiles.length !== dstTiles.length) return;

      srcTiles.forEach((s, i) => {
        const d = dstTiles[i];

        const sPrice = s.querySelector('.m-qty-tile__price');
        const dPrice = d.querySelector('.m-qty-tile__price');
        if (sPrice && dPrice) dPrice.innerHTML = sPrice.innerHTML;

        const sLabel = s.querySelector('.m-qty-tile__label');
        const dLabel = d.querySelector('.m-qty-tile__label');
        if (sLabel && dLabel) dLabel.innerHTML = sLabel.innerHTML;

        // discount badge: add, update, or remove to match the new variant
        const sBadge = s.querySelector('.m-qty-tile__badge');
        let dBadge = d.querySelector('.m-qty-tile__badge');
        if (sBadge) {
          if (!dBadge) {
            dBadge = document.createElement('span');
            d.insertBefore(dBadge, d.firstChild);
          }
          // Copy the class too - the badge carries modifiers such as --extra.
          dBadge.className = sBadge.className;
          dBadge.innerHTML = sBadge.innerHTML;
        } else if (dBadge) {
          dBadge.remove();
        }

        // "Best value" ribbon can move between tiers on a different variant
        const sRibbon = s.querySelector('.m-qty-tile__ribbon');
        let dRibbon = d.querySelector('.m-qty-tile__ribbon');
        if (sRibbon) {
          if (!dRibbon) {
            dRibbon = document.createElement('span');
            dRibbon.className = 'm-qty-tile__ribbon';
            d.insertBefore(dRibbon, d.firstChild);
          }
          dRibbon.innerHTML = sRibbon.innerHTML;
          d.classList.add('m-qty-tile--best');
        } else if (dRibbon) {
          dRibbon.remove();
          d.classList.remove('m-qty-tile--best');
        }

        const qv = s.getAttribute('data-qty-value');
        if (qv !== null) d.setAttribute('data-qty-value', qv);

        // the nudge copy carries money amounts, so it is variant-specific too
        const nudge = s.getAttribute('data-qty-nudge');
        if (nudge === null) d.removeAttribute('data-qty-nudge');
        else d.setAttribute('data-qty-nudge', nudge);
      });

      this.tiles = Array.from(this.querySelectorAll('[data-qty-tile]'));
      this.bindTiles();
      this.syncActiveTile();
    }

    maxFixedQty() {
      let max = 0;
      this.tiles.forEach((t) => {
        if (!t.hasAttribute('data-qty-custom')) {
          const v = parseInt(t.getAttribute('data-qty-value'), 10);
          if (!isNaN(v) && v > max) max = v;
        }
      });
      return max;
    }

    onTileClick(tile) {
      this.tiles.forEach((t) => t.classList.remove('is-active'));
      tile.classList.add('is-active');

      const isCustom = tile.hasAttribute('data-qty-custom');

      if (isCustom) {
        if (this.customWrapper) this.customWrapper.hidden = false;
        if (this.input) {
          const maxTile = this.maxFixedQty();
          let start = maxTile + 1;
          const minAttr = parseInt(this.input.getAttribute('min'), 10);
          if (!isNaN(minAttr) && start < minAttr) start = minAttr;
          if (start < 2) start = 2;

          const current = parseInt(this.input.value, 10);
          if (isNaN(current) || current <= maxTile) {
            this.input.value = start;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.input.focus();
        }
        return;
      }

      if (this.customWrapper) this.customWrapper.hidden = true;

      const qty = parseInt(tile.getAttribute('data-qty-value'), 10);
      if (this.input && !isNaN(qty)) {
        this.input.value = qty;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* Mirrors the variant pickers: "Height: Compact" -> "Quantity: 2". */
    updateSelectedLabel() {
      if (!this.selectedLabel || !this.input) return;
      const qty = parseInt(this.input.value, 10);
      this.selectedLabel.textContent = isNaN(qty) ? this.input.value : qty;
    }

    /* The nudge copy is rendered by Liquid onto each tile (money formatting
       stays in one place), so this only has to surface the active tile's. */
    updateNudge(activeTile) {
      if (!this.nudgeOut) return;
      const text = activeTile ? activeTile.getAttribute('data-qty-nudge') : '';
      this.nudgeOut.textContent = text || '';
    }

    syncActiveTile() {
      if (!this.input) return;
      this.updateSelectedLabel();
      const currentQty = parseInt(this.input.value, 10);
      const match = this.tiles.find(
        (t) => !t.hasAttribute('data-qty-custom') && parseInt(t.getAttribute('data-qty-value'), 10) === currentQty
      );

      this.tiles.forEach((t) => t.classList.remove('is-active'));

      if (match) {
        match.classList.add('is-active');
        this.updateNudge(match);
        if (this.customWrapper) this.customWrapper.hidden = true;
      } else {
        const customTile = this.tiles.find((t) => t.hasAttribute('data-qty-custom'));
        if (customTile) customTile.classList.add('is-active');
        this.updateNudge(customTile);
        if (this.customWrapper) this.customWrapper.hidden = false;
      }
    }
  }
  customElements.define('qty-tile-selector', QtyTileSelector);
}
